import { OfferRowActions } from 'modules/sales-offer/offer/components/OfferRowActions';
import { CellAlign, CellType, HeadCell } from '@dfl/mui-admin-layout';
import { IOffer } from 'modules/sales-offer/offer/interfaces';
import { OFFER_PERMISSIONS } from 'modules/sales-offer/offer/constants/offer.permissions';
import { OfferNameCell } from '../components/OfferNameCell';
import { renderOfferTypeChip } from '../components/OfferTypeChip/OfferTypeChip';
import { renderOfferStatus } from '../components/OfferStatus/OfferStatus';
import { ProgramerDateCell } from 'modules/sales-offer/common/components/ProgramerDateCell';
import { ProductDiscountCell } from 'modules/sales-offer/product-discount/components/ProductDiscountCell';
import { BooleanCell } from 'modules/common/components/BooleanCell';
import OfferReactiveClientRangeCell from '../components/OfferReactiveClientRangeCell/OfferReactiveClientRangeCell';
import PriceCell from 'modules/security/audit-logs/components/TableCells/PriceCell';
import { CostCell } from 'modules/common/components/CostCell';
import { OfferVisiblePicker } from '../components/OfferVisiblePicker';
import { OfferScopeChip } from 'modules/sales-offer/common/components/OfferScopeChip';

export const offerNameColumn: HeadCell<IOffer> = {
  field: 'name',
  headerName: 'offerOrder:fields.name',
  disablePadding: false,
  component: OfferNameCell,
};

export const offerTypeColumn: HeadCell<IOffer> = {
  field: 'type',
  align: CellAlign.CENTER,
  headerName: 'offerOrder:fields.type',
  renderCell: (type: string) => renderOfferTypeChip(type),
};

export const offerDiscountColumn: HeadCell<IOffer> = {
  field: 'discountValue',
  align: CellAlign.CENTER,
  headerName: 'productDiscount:fields.discount',
  renderCell: (_, data) => <ProductDiscountCell data={data?.discountValue} />,
};

export const offerTotalDiscountColumn: HeadCell<any> = {
  field: 'totalDiscount',
  align: CellAlign.CENTER,
  headerName: 'productDiscount:fields.discount',
  renderCell: (_, data) => <CostCell value={data?.totalDiscount} />,
};

export const offerStatusColumn: HeadCell<IOffer> = {
  field: 'status',
  align: CellAlign.CENTER,
  headerName: 'offerOrder:status.title',
  renderCell: (_, data) => renderOfferStatus(data?.fromDate, data?.toDate),
};

export const offerFromDateColumn: HeadCell<any> = {
  field: 'fromDate',
  type: CellType.DATE,
  align: CellAlign.CENTER,
  headerName: 'productDiscount:fields.fromToDate',
  renderCell: (_, data) => <ProgramerDateCell fromDate={data?.fromDate} toDate={data?.toDate} />,
};

export const offerToDateColumn: HeadCell<IOffer> = {
  field: 'toDate',
  headerName: 'offerOrder:fields.toDate',
  type: CellType.DATE,
  width: 200,
};

export const offerAlwaysColumn: HeadCell<IOffer> = {
  field: 'always',
  headerName: 'offerOrder:multipleOffer',
  align: CellAlign.CENTER,
  component: BooleanCell,
};

export const offerIncomeColumn: HeadCell<IOffer> = {
  field: 'totalAmount',
  headerName: 'common:income',
  align: CellAlign.CENTER,
  renderCell: (value, data) => <PriceCell value={value} />,
};

export const offerAveIncomeColumn: HeadCell<IOffer> = {
  field: 'avgAmount',
  headerName: 'common:averageIncome',
  align: CellAlign.CENTER,
  renderCell: (value, data) => <PriceCell value={value} />,
};

export const offerAveDiscountColumn: HeadCell<IOffer> = {
  field: 'avgDiscount',
  headerName: 'reportSalesOffer:summary.avgDiscount',
  align: CellAlign.CENTER,
  renderCell: (value: number) => <CostCell value={value} />,
};

export const offerClientsColumn: HeadCell<IOffer> = {
  field: 'totalClients',
  headerName: 'breadcrumb:clients',
  type: CellType.NUMBER,
  align: CellAlign.CENTER,
};

export const offerRangeColumn: HeadCell = {
  field: 'range',
  headerName: 'offers:fields.range',
  component: OfferReactiveClientRangeCell,
};

export const offerVisibleColumn: HeadCell = {
  field: 'visible',
  headerName: 'common:fields.visible.title',
  component: OfferVisiblePicker,
};

export const offerScopeColumn: HeadCell<IOffer> = {
  field: 'isGlobal',
  headerName: 'offerOrder:scope.columnHeader',
  renderCell: (_, data) => <OfferScopeChip data={data} />,
};

export const offerReactiveClientsColumn: HeadCell = {
  field: 'count',
  headerName: 'offers:fields.clients',
  align: CellAlign.CENTER,
};

export const offerMinAppliedDateColumn: HeadCell = {
  field: 'minAppliedDate',
  type: CellType.DATE,
  align: CellAlign.CENTER,
  headerName: 'offers:fields.minAppliedDate',
};

export const offerMaxAppliedDateColumn: HeadCell = {
  field: 'maxAppliedDate',
  type: CellType.DATE,
  align: CellAlign.CENTER,
  headerName: 'offers:fields.maxAppliedDate',
};

export const offerActionsColumn: HeadCell<IOffer> = {
  field: 'actions',
  sortable: false,
  width: 100,
  permissions: OFFER_PERMISSIONS.OFFER_WRITE,
  headerName: 'common:actions',
  disablePadding: true,
  renderCell: (_, data) => <OfferRowActions data={data} />,
};

export const offerOrdersColumn: HeadCell = {
  field: 'totalOrders',
  headerName: 'breadcrumb:orders',
  type: CellType.NUMBER,
  headerAlign: CellAlign.CENTER,
  align: CellAlign.CENTER,
};

export const offerColumns: Array<HeadCell<any>> = [
  offerNameColumn,
  offerTypeColumn,
  offerDiscountColumn,
  offerStatusColumn,
  offerAlwaysColumn,
  offerScopeColumn,
  offerVisibleColumn,
  offerFromDateColumn,
  offerToDateColumn,
  offerActionsColumn,
];

export const offerByTypeColumns: Array<HeadCell<any>> = [
  offerTypeColumn,
  offerOrdersColumn,
  offerClientsColumn,
  offerIncomeColumn,
  offerAveIncomeColumn,
  offerTotalDiscountColumn,
  offerAveDiscountColumn,
];

export const offerComparativeColumns: Array<HeadCell<any>> = [
  offerNameColumn,
  offerTypeColumn,
  offerOrdersColumn,
  offerClientsColumn,
  offerIncomeColumn,
  offerAveIncomeColumn,
  offerTotalDiscountColumn,
  offerAveDiscountColumn,
  offerMinAppliedDateColumn,
  offerMaxAppliedDateColumn,
];
